import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import './Pricing.css';

const Pricing: React.FC = () => {
    const { t, i18n } = useTranslation();
    const navigate = useNavigate();
    const [billingPeriod, setBillingPeriod] = useState<'monthly' | 'annual'>('monthly');
    const [acceptedTerms, setAcceptedTerms] = useState(false);

    const creditPackages = [
        {
            id: 'student-credit',
            name: 'Forfait Étudiant',
            nameAr: 'باقة الطالب',
            price: 1500,
            questions: 40,
            icon: 'bi-mortarboard',
            color: '#6c5ce7',
            features: [
                'Accès aux ressources de base',
                'Assistant juridique IA',
                '40 questions incluses'
            ],
            featuresAr: [
                'الوصول إلى الموارد الأساسية',
                'المساعد القانوني الذكي',
                '40 سؤال مشمول'
            ]
        },
        {
            id: 'pro-credit',
            name: 'Forfait Professionnel',
            nameAr: 'الباقة المهنية',
            price: 3000,
            questions: 40,
            icon: 'bi-briefcase',
            color: '#0984e3',
            badge: 'Raisonnement avancé',
            badgeAr: 'تفكير متقدم',
            features: [
                'Raisonnement juridique avancé',
                'Analyses approfondies',
                '40 questions incluses'
            ],
            featuresAr: [
                'التفكير القانوني المتقدم',
                'تحليلات معمقة',
                '40 سؤال مشمول'
            ]
        }
    ];

    const subscriptionPlans = [
        {
            id: 'starter',
            name: 'Pack Starter',
            nameAr: 'الباقة الأساسية',
            subtitle: 'Étudiant / Enseignant',
            subtitleAr: 'طالب / أستاذ',
            monthlyPrice: 3000,
            annualPrice: 30000,
            annualSaving: 6000,
            icon: 'bi-book',
            color: '#00b894',
            popular: false,
            features: [
                'Accès aux Codes et textes officiels',
                'Ressources pédagogiques',
                'Assistant juridique IA',
                '120 questions / mois',
                'Tarif spécial sur justificatif'
            ],
            featuresAr: [
                'الوصول إلى القوانين والنصوص الرسمية',
                'الموارد التعليمية',
                'المساعد القانوني الذكي',
                '120 سؤال / شهر',
                'سعر خاص بموجب إثبات'
            ]
        },
        {
            id: 'premium',
            name: 'Pack Premium',
            nameAr: 'الباقة المميزة',
            subtitle: 'Professionnel',
            subtitleAr: 'مهني',
            monthlyPrice: 6000,
            annualPrice: 60000,
            annualSaving: 12000,
            icon: 'bi-star',
            color: '#981934',
            popular: true,
            features: [
                'Accès complet aux Codes et textes',
                'Jurisprudence complète',
                'Doctrine et analyses',
                'Assistant juridique IA avancé',
                '120 questions / mois',
                'Mises à jour illimitées'
            ],
            featuresAr: [
                'الوصول الكامل للقوانين والنصوص',
                'الاجتهاد القضائي الكامل',
                'الفقه والتحليلات',
                'المساعد القانوني الذكي المتقدم',
                '120 سؤال / شهر',
                'تحديثات غير محدودة'
            ]
        },
        {
            id: 'enterprise',
            name: 'Pack Entreprise',
            nameAr: 'باقة المؤسسات',
            subtitle: 'Cabinet / Multi-utilisateurs',
            subtitleAr: 'مكتب / متعدد المستخدمين',
            monthlyPrice: null,
            annualPrice: null,
            icon: 'bi-building',
            color: '#2d3436',
            popular: false,
            isEnterprise: true,
            features: [
                'Accès multi-utilisateurs',
                'Toutes les fonctionnalités Premium',
                'Support dédié',
                'Facturation personnalisée',
                'Formation incluse'
            ],
            featuresAr: [
                'وصول متعدد المستخدمين',
                'جميع ميزات الباقة المميزة',
                'دعم مخصص',
                'فوترة مخصصة',
                'تدريب مضمن'
            ]
        }
    ];

    const isArabic = i18n.language === 'ar';

    return (
        <div className="pricing-page">
            <div className="container py-5">
                {/* Header */}
                <div className="pricing-header text-center mb-5">
                    <h1 className="pricing-title">
                        <i className="bi bi-gem me-3"></i>
                        {t('pricing.title')}
                    </h1>
                    <p className="pricing-subtitle">
                        {t('pricing.subtitle')}
                    </p>
                </div>

                {/* Credit Packages Section */}
                <section className="mb-5">
                    <h2 className="section-title text-center mb-4">
                        <i className="bi bi-coin me-2 text-warning"></i>
                        {t('pricing.creditPackages')}
                    </h2>
                    <p className="text-center text-muted mb-4">
                        {t('pricing.payAsYouGo')}
                    </p>
                    <div className="row justify-content-center g-4">
                        {creditPackages.map(pkg => (
                            <div key={pkg.id} className="col-md-5 col-lg-4">
                                <div className="pricing-card credit-card" style={{ borderTopColor: pkg.color }}>
                                    {pkg.badge && (
                                        <span className="card-badge" style={{ background: pkg.color }}>
                                            {isArabic ? pkg.badgeAr : pkg.badge}
                                        </span>
                                    )}
                                    <div className="card-icon" style={{ color: pkg.color }}>
                                        <i className={`bi ${pkg.icon}`}></i>
                                    </div>
                                    <h3 className="card-name">{isArabic ? pkg.nameAr : pkg.name}</h3>
                                    <div className="card-price">
                                        <span className="amount">{pkg.price.toLocaleString()}</span>
                                        <span className="currency">DA</span>
                                    </div>
                                    <p className="questions-count">
                                        <i className="bi bi-chat-dots me-1"></i>
                                        {pkg.questions} {t('pricing.questions')}
                                    </p>
                                    <ul className="features-list">
                                        {(isArabic ? pkg.featuresAr : pkg.features).map((f, i) => (
                                            <li key={i}><i className="bi bi-check2"></i> {f}</li>
                                        ))}
                                    </ul>
                                    <button className="btn-subscribe" style={{ background: pkg.color }}>
                                        {t('pricing.buyNow')}
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                </section>

                {/* Subscription Plans Section */}
                <section className="mb-5">
                    <h2 className="section-title text-center mb-4">
                        <i className="bi bi-calendar-check me-2 text-primary"></i>
                        {t('pricing.subscriptions')}
                    </h2>

                    {/* Billing Toggle */}
                    <div className="billing-toggle mb-4">
                        <button
                            className={`toggle-btn ${billingPeriod === 'monthly' ? 'active' : ''}`}
                            onClick={() => setBillingPeriod('monthly')}
                        >
                            {t('pricing.monthly')}
                        </button>
                        <button
                            className={`toggle-btn ${billingPeriod === 'annual' ? 'active' : ''}`}
                            onClick={() => setBillingPeriod('annual')}
                        >
                            {t('pricing.annual')}
                            <span className="save-badge">{t('pricing.monthsFree')}</span>
                        </button>
                    </div>

                    <div className="row justify-content-center g-4">
                        {subscriptionPlans.map(plan => (
                            <div key={plan.id} className="col-md-6 col-lg-4">
                                <div className={`pricing-card subscription-card ${plan.popular ? 'popular' : ''}`}
                                    style={{ borderTopColor: plan.color }}>
                                    {plan.popular && (
                                        <span className="popular-badge">
                                            <i className="bi bi-star-fill me-1"></i>
                                            {t('pricing.popular')}
                                        </span>
                                    )}
                                    <div className="card-icon" style={{ color: plan.color }}>
                                        <i className={`bi ${plan.icon}`}></i>
                                    </div>
                                    <h3 className="card-name">{isArabic ? plan.nameAr : plan.name}</h3>
                                    <p className="card-subtitle">{isArabic ? plan.subtitleAr : plan.subtitle}</p>

                                    {plan.isEnterprise ? (
                                        <div className="card-price enterprise">
                                            <span className="custom-text">
                                                {t('pricing.enterprise')}
                                            </span>
                                        </div>
                                    ) : (
                                        <div className="card-price">
                                            <span className="amount">
                                                {(billingPeriod === 'monthly' ? plan.monthlyPrice : plan.annualPrice)?.toLocaleString()}
                                            </span>
                                            <span className="currency">DA</span>
                                            <span className="period">
                                                /{billingPeriod === 'monthly'
                                                    ? t('pricing.month')
                                                    : t('pricing.year')}
                                            </span>
                                        </div>
                                    )}

                                    {billingPeriod === 'annual' && plan.annualSaving && (
                                        <p className="saving-text">
                                            <i className="bi bi-piggy-bank me-1"></i>
                                            {isArabic ? `وفر ${plan.annualSaving.toLocaleString()} دج` : `Économisez ${plan.annualSaving.toLocaleString()} DA`}
                                        </p>
                                    )}

                                    <ul className="features-list">
                                        {(isArabic ? plan.featuresAr : plan.features).map((f, i) => (
                                            <li key={i}><i className="bi bi-check-circle-fill"></i> {f}</li>
                                        ))}
                                    </ul>

                                    <button
                                        className="btn-subscribe"
                                        style={{ background: plan.color }}
                                    >
                                        {plan.isEnterprise
                                            ? t('pricing.requestQuote')
                                            : t('pricing.subscribe')}
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                </section>

                {/* Contact CTA */}
                <div className="contact-cta text-center mt-5">
                    <p>{t('pricing.haveQuestions')}</p>
                    <a href="mailto:contact@berti-law.dz" className="btn btn-outline-primary">
                        <i className="bi bi-envelope me-2"></i>
                        contact@berti-law.dz
                    </a>
                </div>
            </div>
        </div>
    );
};

export default Pricing;
